import {
  AlertCircle,
  Bot,
  ChevronRight,
  FileSearch,
  LoaderCircle,
  PanelRightOpen,
  RefreshCw,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  api,
  normalizeArtifact,
  normalizeEvidence,
  normalizePermission,
  normalizeSnapshot,
  recordFrom,
  subscribeUnauthorized,
  stringList,
  type ArtifactDetail,
  type ArtifactRecord,
  type AsyncState,
  type AttachmentRef,
  type BootstrapData,
  type PermissionRequest,
  type SessionReplayGap,
  type SessionSnapshot,
  type SessionSummary,
  type SseEvent,
} from "./api";
import { Composer } from "./components/Composer";
import { Inspector } from "./components/Inspector";
import { MarkdownReport } from "./components/MarkdownReport";
import { SessionRail } from "./components/SessionRail";
import { WorkspaceBrowser } from "./components/WorkspaceBrowser";
import { useSessionStream } from "./hooks/useSessionStream";
import {
  applyEventToTurns,
  bootstrapLocal,
  bootstrapModel,
  bootstrapPermission,
  bootstrapWorkspace,
  createRequestId,
  formatDate,
} from "./utils";
import { CadenceSurface } from "./CadenceSurface";
import { DnaEntryTransition } from "./DnaEntryTransition";
import { LoginScreen } from "./LoginScreen";

interface Notice {
  type: "error" | "success";
  text: string;
}

const ATTACHMENT_ONLY_PROMPT = "请分析所附材料，并基于证据给出结构化研究报告。";

export default function App() {
  const query = new URLSearchParams(window.location.search);
  const surface = query.get("surface");
  const demoRequested = import.meta.env.DEV && query.get("demo") === "1";
  const [authState, setAuthState] = useState<
    "checking" | "authenticated" | "unauthenticated"
  >(demoRequested ? "authenticated" : "checking");
  const [showEntryTransition, setShowEntryTransition] = useState(false);
  const [sessionError, setSessionError] = useState<string>();

  useEffect(() => {
    if (demoRequested) return undefined;
    let active = true;
    const markUnauthenticated = (message?: string) => {
      clearCadenceFrontendState();
      if (active) {
        if (message) setSessionError(message);
        setShowEntryTransition(false);
        setAuthState("unauthenticated");
      }
    };
    const unsubscribe = subscribeUnauthorized(() =>
      markUnauthenticated("登录状态已失效，请重新登录。"),
    );

    void api
      .getAuthSession()
      .then((session) => {
        if (!active) return;
        if (session.authenticated) {
          setSessionError(undefined);
          setShowEntryTransition(true);
          setAuthState("authenticated");
        } else {
          markUnauthenticated();
        }
      })
      .catch((cause) => {
        if (!active) return;
        setSessionError(
          errorMessage(cause, "无法验证当前会话，请登录后重试。"),
        );
        markUnauthenticated();
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [demoRequested]);

  if (authState !== "authenticated") {
    return (
      <LoginScreen
        checking={authState === "checking"}
        initialError={sessionError}
        onAuthenticated={() => {
          setSessionError(undefined);
          setShowEntryTransition(true);
          setAuthState("authenticated");
        }}
      />
    );
  }

  return (
    <>
      {surface === "legacy" ? <LegacyWorkbench /> : <CadenceSurface />}
      {showEntryTransition && (
        <DnaEntryTransition
          onComplete={() => setShowEntryTransition(false)}
        />
      )}
    </>
  );
}

function LegacyWorkbench() {
  const [bootstrap, setBootstrap] = useState<BootstrapData>();
  const [bootstrapState, setBootstrapState] = useState<AsyncState>("loading");
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [sessionsState, setSessionsState] = useState<AsyncState>("loading");
  const [sessionsError, setSessionsError] = useState<string>();
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const [snapshot, setSnapshot] = useState<SessionSnapshot>();
  const [snapshotState, setSnapshotState] = useState<AsyncState>("idle");
  const [snapshotError, setSnapshotError] = useState<string>();
  const [artifacts, setArtifacts] = useState<ArtifactRecord[]>([]);
  const [events, setEvents] = useState<SseEvent[]>([]);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [workspaceBrowserOpen, setWorkspaceBrowserOpen] = useState(false);
  const [composerValue, setComposerValue] = useState("");
  const [attachments, setAttachments] = useState<AttachmentRef[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [composerError, setComposerError] = useState<string>();
  const [notice, setNotice] = useState<Notice>();

  const loadInitialData = useCallback(async () => {
    setBootstrapState("loading");
    setSessionsState("loading");
    setSessionsError(undefined);
    const [bootstrapResult, sessionsResult] = await Promise.allSettled([
      api.bootstrap(),
      api.listSessions(),
    ]);

    if (bootstrapResult.status === "fulfilled") {
      setBootstrap(bootstrapResult.value);
      setBootstrapState("ready");
    } else {
      setBootstrapState("error");
    }

    if (sessionsResult.status === "fulfilled") {
      setSessions(sessionsResult.value);
      setSessionsState("ready");
      const currentId =
        bootstrapResult.status === "fulfilled"
          ? bootstrapResult.value.current_session_id
          : undefined;
      setSelectedSessionId(
        (current) =>
          current ??
          sessionsResult.value.find((session) => session.id === currentId)?.id,
      );
    } else {
      setSessionsState("error");
      setSessionsError(
        errorMessage(sessionsResult.reason, "会话列表加载失败。"),
      );
    }
  }, []);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  const refreshArtifactsAndEvidence = useCallback(async (sessionId: string) => {
    const listedArtifacts = await api.listArtifacts(sessionId);
    setArtifacts((current) => mergeArtifacts(current, listedArtifacts));

    const evidenceArtifact = listedArtifacts.find(
      (artifact) =>
        artifact.name.toLowerCase() === "evidence.json",
    );
    if (!evidenceArtifact) return;

    const detail = await api.getArtifact(sessionId, evidenceArtifact.id);
    const raw = recordFrom(detail.raw);
    if (!Object.keys(raw).length) return;
    const annotation = recordFrom(raw.annotation);
    const quarantined = listedArtifacts.some((artifact) =>
      artifact.name.toLowerCase().includes("quarantined"),
    );
    const evidence = normalizeEvidence(raw, evidenceArtifact.id);
    const externalNetworkUsed =
      typeof annotation.external_network_used === "boolean"
        ? annotation.external_network_used
        : undefined;
    const artifactWarnings = stringList(raw.warnings ?? raw.warning);

    setSnapshot((current) =>
      current
        ? {
            ...current,
            evidence: [
              {
                ...evidence,
                status: quarantined ? "quarantined" : evidence.status,
              },
            ],
            warnings: Array.from(
              new Set([...current.warnings, ...artifactWarnings]),
            ),
            externalNetworkUsed:
              externalNetworkUsed ?? current.externalNetworkUsed,
          }
        : current,
    );
  }, []);

  const loadSelectedSession = useCallback(
    async (sessionId: string) => {
      setSnapshotState("loading");
      setSnapshotError(undefined);
      setEvents([]);
      try {
        const nextSnapshot = await api.getSnapshot(sessionId);
        setSnapshot(nextSnapshot);
        setArtifacts(nextSnapshot.artifacts);
        setSnapshotState("ready");
        try {
          await refreshArtifactsAndEvidence(sessionId);
        } catch {
          // Snapshot artifacts remain usable if the dedicated endpoint is unavailable.
        }
      } catch (reason) {
        setSnapshot(undefined);
        setArtifacts([]);
        setSnapshotState("error");
        setSnapshotError(errorMessage(reason, "会话快照加载失败。"));
      }
    },
    [refreshArtifactsAndEvidence],
  );

  useEffect(() => {
    setAttachments([]);
    setComposerError(undefined);
    if (!selectedSessionId) {
      setSnapshot(undefined);
      setArtifacts([]);
      setEvents([]);
      setSnapshotState("idle");
      return;
    }
    void loadSelectedSession(selectedSessionId);
  }, [loadSelectedSession, selectedSessionId]);

  const handleSseEvent = useCallback(
    (event: SseEvent) => {
      const eventType = event.type.toLowerCase();
      const terminal =
        eventType.includes("completed") ||
        eventType.includes("failed") ||
        eventType.includes("cancelled") ||
        eventType.includes("canceled");
      const artifactEvent = eventType.includes("artifact")
        ? normalizeArtifact(
            event.payload,
            event.event_id ?? `artifact-${event.seq}`,
          )
        : undefined;
      if (artifactEvent) {
        setArtifacts((currentArtifacts) =>
          upsertBy(currentArtifacts, artifactEvent, (entry) => entry.id),
        );
      }
      setEvents((current) => {
        if (
          current.some(
            (item) => item.event_id && item.event_id === event.event_id,
          )
        )
          return current;
        if (current.some((item) => item.seq === event.seq)) return current;
        return [...current, event].slice(-500);
      });
      if (eventType === "session.snapshot") {
        const recovered = normalizeSnapshot(event.payload, event.session_id);
        setSnapshot(recovered);
        setArtifacts(recovered.artifacts);
        if (selectedSessionId) {
          void refreshArtifactsAndEvidence(selectedSessionId).catch(
            () => undefined,
          );
        }
        return;
      }
      setSnapshot((current) => {
        if (!current) return current;
        const type = eventType;
        let permissions = current.permissions;
        let evidence = current.evidence;
        let nextArtifacts = current.artifacts;

        if (type.includes("permission") || type.includes("approval")) {
          const request = normalizePermission(
            event.payload,
            event.event_id ?? `permission-${event.seq}`,
          );
          if (
            type.includes("required") ||
            type.includes("request") ||
            type.includes("pending")
          ) {
            permissions = upsertBy(
              permissions,
              request,
              (item) => item.requestId,
            );
          } else if (
            type.includes("approved") ||
            type.includes("denied") ||
            type.includes("resolved")
          ) {
            permissions = permissions.filter(
              (item) => item.requestId !== request.requestId,
            );
          }
        }

        if (type.includes("evidence")) {
          const item = normalizeEvidence(
            event.payload,
            event.event_id ?? `evidence-${event.seq}`,
          );
          evidence = upsertBy(evidence, item, (entry) => entry.id);
        }

        if (artifactEvent) {
          nextArtifacts = upsertBy(
            nextArtifacts,
            artifactEvent,
            (entry) => entry.id,
          );
        }

        return {
          ...current,
          turns: applyEventToTurns(current.turns, event),
          permissions,
          evidence,
          artifacts: nextArtifacts,
          activeTurnId: terminal
            ? undefined
            : (event.turn_id ?? current.activeTurnId),
        };
      });
      if (terminal && selectedSessionId) {
        void refreshArtifactsAndEvidence(selectedSessionId).catch(
          () => undefined,
        );
      }
    },
    [refreshArtifactsAndEvidence, selectedSessionId],
  );
  const handleReplayGap = useCallback(
    (gap: SessionReplayGap) => {
      if (gap.sessionId !== selectedSessionId) return;
      setSnapshot(gap.snapshot);
      setArtifacts(gap.snapshot.artifacts);
      setEvents([]);
      setSnapshotState("ready");
      setSnapshotError(undefined);
      if (gap.snapshot.session) {
        setSessions((current) =>
          upsertBy(
            current,
            gap.snapshot.session as SessionSummary,
            (item) => item.id,
          ),
        );
      }
    },
    [selectedSessionId],
  );

  const stream = useSessionStream({
    api,
    sessionId: selectedSessionId,
    initialSeq: snapshot?.lastSeq ?? 0,
    enabled: snapshotState === "ready",
    onEvent: handleSseEvent,
    onReplayGap: handleReplayGap,
  });

  const selectedSession = sessions.find(
    (session) => session.id === selectedSessionId,
  );
  const activeTurnId = snapshot?.activeTurnId;
  const running =
    submitting ||
    Boolean(activeTurnId) ||
    snapshot?.session?.running === true ||
    selectedSession?.running === true;

  const createSession = async () => {
    setNotice(undefined);
    try {
      const session = await api.createSession({ mode: "new" });
      setSessions((current) => upsertBy(current, session, (item) => item.id));
      setSessionsState("ready");
      setSelectedSessionId(session.id);
    } catch (reason) {
      setNotice({
        type: "error",
        text: errorMessage(reason, "无法新建会话。"),
      });
    }
  };

  const resumeSession = async (session: SessionSummary) => {
    if (!session.path || session.running) {
      setSelectedSessionId(session.id);
      return;
    }
    setNotice(undefined);
    try {
      const resumed = await api.createSession({
        mode: "resume",
        path: session.path,
      });
      setSessions((current) => upsertBy(current, resumed, (item) => item.id));
      setSelectedSessionId(resumed.id);
    } catch (reason) {
      setNotice({
        type: "error",
        text: errorMessage(reason, "会话恢复失败。"),
      });
    }
  };

  const submitTurn = async () => {
    if (!selectedSessionId || running) return;
    const input =
      composerValue.trim() ||
      (attachments.length ? ATTACHMENT_ONLY_PROMPT : "");
    if (!input && !attachments.length) return;
    const requestId = createRequestId();
    const optimisticTurn = {
      id: requestId,
      requestId,
      input,
      output: "",
      status: "running" as const,
      createdAt: Date.now(),
      attachments,
      raw: {},
    };

    setSubmitting(true);
    setComposerError(undefined);
    setSnapshot((current) =>
      current
        ? {
            ...current,
            turns: [...current.turns, optimisticTurn],
            activeTurnId: requestId,
          }
        : current,
    );

    try {
      const result = await api.sendTurn(selectedSessionId, {
        request_id: requestId,
        input,
        attachments,
      });
      const turnId = result.turn_id ?? requestId;
      setSnapshot((current) => {
        if (!current) return current;
        const alreadyExists = current.turns.some(
          (turn) => turn.id === turnId && turn.id !== requestId,
        );
        const turns = alreadyExists
          ? current.turns.filter((turn) => turn.id !== requestId)
          : current.turns.map((turn) =>
              turn.id === requestId ? { ...turn, id: turnId } : turn,
            );
        return { ...current, turns, activeTurnId: turnId };
      });
      setComposerValue("");
      setAttachments([]);
    } catch (reason) {
      const message = errorMessage(reason, "研究请求提交失败。");
      setComposerError(message);
      setSnapshot((current) =>
        current
          ? {
              ...current,
              activeTurnId: undefined,
              turns: current.turns.map((turn) =>
                turn.id === requestId
                  ? { ...turn, status: "error", error: message }
                  : turn,
              ),
            }
          : current,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const stopTurn = async () => {
    if (!selectedSessionId || !activeTurnId) return;
    setComposerError(undefined);
    try {
      await api.cancelTurn(selectedSessionId, activeTurnId);
      setSnapshot((current) =>
        current
          ? {
              ...current,
              activeTurnId: undefined,
              turns: current.turns.map((turn) =>
                turn.id === activeTurnId
                  ? { ...turn, status: "cancelled" }
                  : turn,
              ),
            }
          : current,
      );
    } catch (reason) {
      setComposerError(errorMessage(reason, "停止请求失败。"));
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (!selectedSessionId) return;
    setUploading(true);
    setComposerError(undefined);
    try {
      const uploaded = await api.uploadFiles(selectedSessionId, files);
      setAttachments((current) => mergeAttachments(current, uploaded));
    } catch (reason) {
      setComposerError(errorMessage(reason, "文件上传失败。"));
    } finally {
      setUploading(false);
    }
  };

  const listWorkspace = useCallback(
    async (path: string) => {
      if (!selectedSessionId) throw new Error("请先选择会话。");
      return api.listFiles(selectedSessionId, path);
    },
    [selectedSessionId],
  );

  const decidePermission = async (
    request: PermissionRequest,
    decision: "approve" | "deny",
  ) => {
    if (!selectedSessionId) return;
    try {
      await api.decidePermission(
        selectedSessionId,
        request.requestId,
        decision,
      );
      setSnapshot((current) =>
        current
          ? {
              ...current,
              permissions: current.permissions.filter(
                (item) => item.requestId !== request.requestId,
              ),
            }
          : current,
      );
      setNotice({
        type: "success",
        text: decision === "approve" ? "已批准本次操作。" : "已拒绝本次操作。",
      });
    } catch (reason) {
      setNotice({
        type: "error",
        text: errorMessage(reason, "权限决策提交失败。"),
      });
    }
  };

  const loadArtifact = useCallback(
    async (artifact: ArtifactRecord): Promise<ArtifactDetail> => {
      if (!selectedSessionId) throw new Error("请先选择会话。");
      return api.getArtifact(selectedSessionId, artifact.id);
    },
    [selectedSessionId],
  );

  const artifactHref = useCallback(
    (artifact: ArtifactRecord) =>
      selectedSessionId ? api.artifactUrl(selectedSessionId, artifact.id) : "#",
    [selectedSessionId],
  );

  const streamLabel = streamStatusLabel(stream.state, api.streamSupported);

  return (
    <div className={`app-shell ${railCollapsed ? "rail-collapsed" : ""}`}>
      <header className="app-header">
        <div className="brand-block">
          <span className="brand-mark" aria-hidden="true">
            PM
          </span>
          <div>
            <strong>PingMedical</strong>
            <span>科研工作台</span>
          </div>
        </div>

        <div className="runtime-status" aria-label="工作台状态">
          <StatusItem
            label="工作区"
            value={bootstrapWorkspace(bootstrap)}
            loading={bootstrapState === "loading"}
          />
          <StatusItem
            label="模型"
            value={bootstrapModel(bootstrap)}
            loading={bootstrapState === "loading"}
          />
          <StatusItem
            label="运行"
            value={bootstrapLocal(bootstrap)}
            loading={bootstrapState === "loading"}
          />
          <StatusItem
            label="权限"
            value={bootstrapPermission(bootstrap)}
            loading={bootstrapState === "loading"}
          />
        </div>

        <button
          className="secondary-button inspector-toggle"
          type="button"
          onClick={() => setInspectorOpen((open) => !open)}
        >
          <PanelRightOpen size={16} />
          检查器
        </button>
      </header>

      <div className="workbench">
        <SessionRail
          collapsed={railCollapsed}
          sessions={sessions}
          sessionsState={sessionsState}
          selectedSessionId={selectedSessionId}
          error={sessionsError}
          onCollapse={() => setRailCollapsed((collapsed) => !collapsed)}
          onNewSession={() => void createSession()}
          onSelect={(sessionId) => {
            const session = sessions.find((item) => item.id === sessionId);
            if (session) {
              void resumeSession(session);
            } else {
              setSelectedSessionId(sessionId);
            }
          }}
          onResume={(session) => void resumeSession(session)}
        />

        <main className="research-pane">
          <div className="research-toolbar">
            <div>
              <span className="toolbar-kicker">当前会话</span>
              <h1>{selectedSession?.title ?? "尚未选择会话"}</h1>
            </div>
            <div
              className={`stream-indicator stream-${stream.state}`}
              aria-live="polite"
            >
              {stream.state === "open" ? (
                <Wifi size={14} />
              ) : (
                <WifiOff size={14} />
              )}
              {streamLabel}
              {stream.lastSeq > 0 && <span>seq {stream.lastSeq}</span>}
            </div>
          </div>

          {notice && (
            <div
              className={`notice notice-${notice.type}`}
              role={notice.type === "error" ? "alert" : "status"}
            >
              {notice.type === "error" ? (
                <AlertCircle size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
              <span>{notice.text}</span>
              <button type="button" onClick={() => setNotice(undefined)}>
                关闭
              </button>
            </div>
          )}

          <div className="report-scroll">
            <ReportContent
              selectedSessionId={selectedSessionId}
              state={snapshotState}
              snapshot={snapshot}
              error={snapshotError}
              onCreate={() => void createSession()}
              onRetry={() =>
                selectedSessionId && void loadSelectedSession(selectedSessionId)
              }
            />
          </div>

          <Composer
            value={composerValue}
            attachments={attachments}
            disabled={
              !selectedSessionId ||
              snapshotState === "loading" ||
              snapshotState === "error"
            }
            running={running}
            uploading={uploading}
            error={composerError}
            onChange={setComposerValue}
            onOpenWorkspace={() => setWorkspaceBrowserOpen(true)}
            onUpload={(files) => void uploadFiles(files)}
            onRemoveAttachment={(index) =>
              setAttachments((current) =>
                current.filter((_, itemIndex) => itemIndex !== index),
              )
            }
            onSubmit={() => void submitTurn()}
            onStop={() => void stopTurn()}
          />
        </main>

        <Inspector
          events={events}
          evidence={snapshot?.evidence ?? []}
          artifacts={artifacts}
          permissions={snapshot?.permissions ?? []}
          externalNetworkUsed={snapshot?.externalNetworkUsed}
          warnings={snapshot?.warnings ?? []}
          streamLabel={streamLabel}
          narrowOpen={inspectorOpen}
          loadArtifact={loadArtifact}
          artifactHref={artifactHref}
          onPermission={(request, decision) =>
            void decidePermission(request, decision)
          }
          onCloseNarrow={() => setInspectorOpen(false)}
        />
      </div>

      <WorkspaceBrowser
        open={workspaceBrowserOpen}
        load={listWorkspace}
        onClose={() => setWorkspaceBrowserOpen(false)}
        onAttach={(attachment) => {
          setAttachments((current) => mergeAttachments(current, [attachment]));
          setWorkspaceBrowserOpen(false);
        }}
      />
    </div>
  );
}

function StatusItem({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading: boolean;
}) {
  return (
    <div className="status-item">
      <span>{label}</span>
      <strong title={value}>{loading ? "读取中" : value}</strong>
    </div>
  );
}

function ReportContent({
  selectedSessionId,
  state,
  snapshot,
  error,
  onCreate,
  onRetry,
}: {
  selectedSessionId?: string;
  state: AsyncState;
  snapshot?: SessionSnapshot;
  error?: string;
  onCreate(): void;
  onRetry(): void;
}) {
  if (!selectedSessionId) {
    return (
      <div className="hero-empty">
        <div className="empty-mark">
          <FileSearch size={26} />
        </div>
        <h2>开始一项可追溯的研究任务</h2>
        <p>
          新建会话后，可引用工作区材料或上传文件，并在右侧查看执行、证据和产物。
        </p>
        <button className="primary-button" type="button" onClick={onCreate}>
          新建会话
        </button>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div className="loading-report" aria-label="正在加载会话">
        <LoaderCircle className="spin" size={22} />
        <strong>正在恢复研究记录</strong>
        <span>读取对话、报告与证据索引…</span>
        <div className="report-skeleton">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="hero-empty error-state">
        <div className="empty-mark">
          <AlertCircle size={25} />
        </div>
        <h2>会话加载失败</h2>
        <p>{error ?? "无法读取当前会话快照。"}</p>
        <button className="secondary-button" type="button" onClick={onRetry}>
          <RefreshCw size={15} />
          重试
        </button>
      </div>
    );
  }

  if (!snapshot?.turns.length) {
    return (
      <div className="hero-empty compact-empty">
        <div className="empty-mark">
          <Bot size={25} />
        </div>
        <h2>会话已就绪</h2>
        <p>在下方描述研究问题。回答将以报告为主，而不是气泡聊天形式。</p>
      </div>
    );
  }

  return (
    <div className="turn-list">
      {snapshot.warnings.length > 0 && (
        <div className="report-warning-list" role="note">
          {snapshot.warnings.map((warning) => (
            <div key={warning}>
              <AlertCircle size={14} />
              {warning}
            </div>
          ))}
        </div>
      )}
      {snapshot.turns.map((turn, index) => (
        <article className="turn-block" key={`${turn.id}-${index}`}>
          {turn.input && (
            <section className="research-question">
              <div className="section-label">研究问题</div>
              <p>{turn.input}</p>
              {turn.attachments.length > 0 && (
                <div className="turn-attachments">
                  {turn.attachments.map((attachment, attachmentIndex) => (
                    <span key={`${attachment.name}-${attachmentIndex}`}>
                      {attachment.name}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className={`report-card report-status-${turn.status}`}>
            <header>
              <div>
                <span className="section-label">研究报告</span>
                <strong>{turnStatusLabel(turn.status)}</strong>
              </div>
              {turn.createdAt && <time>{formatDate(turn.createdAt)}</time>}
            </header>
            {turn.output ? (
              <MarkdownReport content={turn.output} />
            ) : turn.status === "running" || turn.status === "pending" ? (
              <div className="running-report" aria-live="polite">
                <LoaderCircle className="spin" size={18} />
                <span>正在组织报告与证据…</span>
              </div>
            ) : (
              <div className="empty-output">本轮没有返回报告内容。</div>
            )}
            {turn.status === "running" && turn.output && (
              <span className="stream-caret" aria-label="仍在生成" />
            )}
            {turn.error && (
              <div className="report-error">
                <AlertCircle size={15} />
                {turn.error}
              </div>
            )}
          </section>
        </article>
      ))}
    </div>
  );
}

function turnStatusLabel(status: string): string {
  if (status === "running") return "执行中";
  if (status === "pending") return "等待执行";
  if (status === "error") return "执行失败";
  if (status === "cancelled") return "已停止";
  return "已完成";
}

function streamStatusLabel(state: string, supported: boolean): string {
  if (!supported) return "开发预览";
  if (state === "open") return "事件流已连接";
  if (state === "connecting") return "正在连接事件流";
  if (state === "reconnecting") return "事件流重连中";
  if (state === "error") return "事件流异常";
  return "事件流未连接";
}

function mergeArtifacts(
  current: ArtifactRecord[],
  incoming: ArtifactRecord[],
): ArtifactRecord[] {
  return incoming.reduce(
    (items, item) => upsertBy(items, item, (artifact) => artifact.id),
    current,
  );
}

function mergeAttachments(
  current: AttachmentRef[],
  incoming: AttachmentRef[],
): AttachmentRef[] {
  const key = (item: AttachmentRef) =>
    item.id ?? item.path ?? `${item.kind}:${item.name}`;
  return incoming.reduce((items, item) => upsertBy(items, item, key), current);
}

function upsertBy<T>(items: T[], item: T, key: (value: T) => string): T[] {
  const itemKey = key(item);
  const index = items.findIndex((value) => key(value) === itemKey);
  if (index < 0) return [...items, item];
  return items.map((value, itemIndex) => (itemIndex === index ? item : value));
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

function clearCadenceFrontendState() {
  try {
    const keys = Array.from(
      { length: window.localStorage.length },
      (_, index) => window.localStorage.key(index),
    );
    keys.forEach((key) => {
      if (key?.startsWith("cadence.")) window.localStorage.removeItem(key);
    });
  } catch {
    // Browser storage is best effort and may be disabled.
  }
}
